import {
  CloudPolicy,
  CloudPolicyType,
  CloudType,
  PolicyExecutionAttempt,
  PolicyExecutionRecord,
  PolicyExecutionStep,
  PolicyRunStatus,
  PolicyStatus,
} from '../types/policies';

const severityLabels: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => (item === null || item === undefined ? '' : String(item).trim()))
    .filter(Boolean);
};

const toTargetArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const raw = value.map(item => (item === null || item === undefined ? '' : String(item).trim()));
  if (raw.some(item => item === '' || item === '*')) return ['*'];
  return raw.filter(Boolean);
};

export const normalizePolicySeverity = (severity: unknown): string => {
  const raw = String(severity || 'Medium').trim();
  return severityLabels[raw.toLowerCase()] || raw || 'Medium';
};

const RUN_STATUS_ALIASES: Record<string, PolicyRunStatus> = {
  SUCCESS: 'SUCCESS',
  COMPLETED: 'SUCCESS',
  RUNNING: 'RUNNING',
  ENABLING: 'RUNNING',
  DISABLING: 'RUNNING',
};

const toRunStatus = (value: unknown): PolicyRunStatus => RUN_STATUS_ALIASES[String(value || '').toUpperCase()] || 'FAILED';

const toStep = (step: any, index: number): PolicyExecutionStep | null => {
  if (!step || typeof step !== 'object' || !step.command) return null;
  const legacyOutput = step.output && typeof step.output === 'object' ? step.output : null;
  const exitCode = legacyOutput ? legacyOutput.exitCode : step.exitCode;
  const output = legacyOutput
    ? [legacyOutput.stdout, legacyOutput.stderr].filter(text => typeof text === 'string' && text.trim()).join('\n')
    : step.output;
  return {
    step: Number(step.step) || index + 1,
    command: String(step.command),
    purpose: step.purpose || step.reason || '',
    exitCode: typeof exitCode === 'number' ? exitCode : undefined,
    output: typeof output === 'string' ? output : '',
  };
};

const toAttempts = (value: any, status: PolicyRunStatus): PolicyExecutionAttempt[] => {
  if (Array.isArray(value.attempts)) {
    return value.attempts.map((attempt: any, index: number) => ({
      attempt: Number(attempt?.attempt) || index + 1,
      status: toRunStatus(attempt?.status),
      reason: attempt?.reason || undefined,
      steps: (Array.isArray(attempt?.steps) ? attempt.steps : [])
        .map(toStep)
        .filter(Boolean) as PolicyExecutionStep[],
    }));
  }
  const legacySteps = Array.isArray(value.steps) ? value.steps : [];
  const legacyError = legacySteps.find((step: any) => step?.error)?.error;
  const lastReason = [...legacySteps].reverse().find((step: any) => step?.reason)?.reason;
  return [{
    attempt: 1,
    status,
    reason: legacyError || lastReason,
    steps: legacySteps.map(toStep).filter(Boolean) as PolicyExecutionStep[],
  }];
};

export const normalizeExecutionHistory = (history: unknown): PolicyExecutionRecord[] => {
  const entries: [string, any][] = Array.isArray(history)
    ? history.map((value, index) => [String(value?.id || `execution-${index}`), value])
    : history && typeof history === 'object'
      ? Object.entries(history as Record<string, any>)
      : [];

  return entries
    .filter(([, value]) => value && typeof value === 'object')
    .map(([id, value]) => {
      const status = toRunStatus(value.status ?? value.result);
      const attempts = toAttempts(value, status);
      const failedAttempt = [...attempts].reverse().find(attempt => attempt.reason);
      return {
        id,
        mode: String(value.mode || 'ENABLE').toUpperCase(),
        status,
        reason: value.reason || (status === 'FAILED' ? failedAttempt?.reason : undefined),
        startedAt: value.startedAt || value.timestamp || value.createdAt,
        finishedAt: value.finishedAt,
        trigger: value.trigger,
        attempts,
      };
    })
    .sort((first, second) => new Date(second.startedAt || 0).getTime() - new Date(first.startedAt || 0).getTime());
};

export const getPolicyCommands = (policyData: any): any[] => {
  if (!policyData || typeof policyData !== 'object') return [];
  if (Array.isArray(policyData.commands)) return policyData.commands;
  if (Array.isArray(policyData.actions)) return policyData.actions;
  return [];
};

export const normalizeCloudPolicy = (
  policy: any,
  fallbackIndex = 0,
  fallbackCloudType?: CloudType,
): CloudPolicy => {
  const policyId = policy?.policyId ? String(policy.policyId) : undefined;
  const id = policy?.id ?? policyId ?? `policy-${Date.now()}-${fallbackIndex}`;
  const cloudPolicyType = (policy?.cloudPolicyType || 'GUARDRAIL') as CloudPolicyType;
  const policyStatus = (policy?.policyStatus || 'INACTIVE') as PolicyStatus;

  return {
    ...policy,
    id,
    policyId,
    title: policy?.title || 'Untitled policy',
    description: policy?.description || '',
    severity: normalizePolicySeverity(policy?.severity),
    cloudPolicyType,
    policyStatus,
    includedUsers: toTargetArray(policy?.includedUsers),
    includedNHIs: toTargetArray(policy?.includedNHIs),
    includedResources: toTargetArray(policy?.includedResources),
    excludedUsers: toTargetArray(policy?.excludedUsers),
    excludedNHIs: toTargetArray(policy?.excludedNHIs),
    excludedResources: toTargetArray(policy?.excludedResources),
    policyTargetTypes: Array.isArray(policy?.policyTargetTypes) ? policy.policyTargetTypes : (policy?.policyTargetType ? [policy.policyTargetType] : []),
    cloudType: (policy?.cloudType || fallbackCloudType || 'AWS') as CloudType,
    policyEnableData: policy?.policyEnableData || {},
    policyDisableData: policy?.policyDisableData || {},
    executionHistory: normalizeExecutionHistory(policy?.executionHistory),
    policyMetadata: policy?.policyMetadata || {},
    linkedAlerts: toStringArray(policy?.linkedAlerts),
    isDefault: Boolean(policy?.isDefault),
    tenantName: policy?.tenantName,
    cloudAccountId: policy?.cloudAccountId,
    lastExecutionStatus: policy?.lastExecutionStatus ? toRunStatus(policy.lastExecutionStatus) : null,
    lastExecutionReason: policy?.lastExecutionReason || null,
    lastExecutedAt: policy?.lastExecutedAt || null,
  };
};

export const normalizeCloudPolicies = (policies: any, fallbackCloudType?: CloudType): CloudPolicy[] => {
  if (!Array.isArray(policies)) return [];
  return policies.map((policy, index) => normalizeCloudPolicy(policy, index, fallbackCloudType));
};
